import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ActionIcon, Alert, Badge, Box, Button, Checkbox, Divider,Group, Modal, ScrollArea, Select, Stack, Switch, Table, Text, Textarea, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconAlertCircle, IconCheck, IconDownload, IconEye, IconFileText, IconId, IconMouse,IconStethoscope, IconX } from "@tabler/icons-react";
import dayjs from "dayjs";

import { AppLoader } from "@components/AppLoader";
import { FilePreviewModal } from "@components/FilePreviewModal";

import { useApproveSession } from "@hooks/mutations/onboarding/useApproveSession";
import { useRejectSession } from "@hooks/mutations/onboarding/useRejectSession";
import { useRequestVerificationChanges } from "@hooks/mutations/onboarding/useRequestVerificationChanges";
import { useVerifySession } from "@hooks/mutations/onboarding/useVerifySession";
import { useGetQueue } from "@hooks/queries/onboarding/useGetQueue";
import { usePrimarySpecializations, primarySpecializationOptions } from "@hooks/queries/profile/usePrimarySpecializations";
import { api, accountDocumentUrl } from "@utils/api";

import { useAuth } from "@contexts/auth";

import { StyledVerificationQueueTab } from "./VerificationQueueTab.styled";


export const VerificationQueueTab = () => {
  const { t } = useTranslation();
  const { token, can } = useAuth();
  const [statusFilter, setStatusFilter] = useState<string | null>("approved");
  const [verificationFilter, setVerificationFilter] = useState<string | null>("pending");
  const { data: queueData, isLoading } = useGetQueue(statusFilter || undefined, verificationFilter || undefined);
  const { data: primarySpecializations } = usePrimarySpecializations();
  const specializationLabel = (session: any) =>
    primarySpecializationOptions(primarySpecializations).find(item => item.value === session.primary_specialization_id)?.label
    || session.specialization || "-";
  const { mutate: approveSession, isLoading: isApproving } = useApproveSession();
  const { mutate: rejectSession, isLoading: isRejecting } = useRejectSession();
  const { mutate: verifySession, isLoading: isVerifying } = useVerifySession();
  const { mutate: requestVerificationChanges, isLoading: isRequestingChanges } = useRequestVerificationChanges();

  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [specializationError, setSpecializationError] = useState("");
  const [isReviewModalOpen, { open: openReviewModal, close: closeReviewModal }] = useDisclosure(false);
  const [isRejectModalOpen, { open: openRejectModal, close: closeRejectModal }] = useDisclosure(false);
  const [rejectReason, setRejectReason] = useState("");
  const [publishProfile, setPublishProfile] = useState(true);
  const [verificationChecks, setVerificationChecks] = useState<Record<string, boolean>>({});

  const checklist = [
    ["identity_reviewed", "Identity reviewed"],
    ["professional_category_confirmed", "Professional category confirmed"],
    ["license_reviewed", "Licence reviewed"],
    ["qualification_reviewed", "Qualification reviewed"],
    ["services_reviewed", "Services and prices reviewed"],
    ["availability_reviewed", "Availability reviewed"],
    ["contract_reviewed", "Signed contract reviewed"],
  ] as const;

  const [previewDocs, setPreviewDocs] = useState<any[]>([]);
  const [isPreviewModalOpen, { open: openPreviewModal, close: closePreviewModal }] = useDisclosure(false);

  const handleRowClick = (session: any) => {
    setSelectedSession(session);
    setVerificationChecks(session.verification_checks || {});
    setPublishProfile(can("profiles.publish") || session.profile_visibility === "public");
    openReviewModal();
  };

  const handleVerify = () => {
    if (!selectedSession) return;
    verifySession({
      sessionId: selectedSession._id,
      checks: verificationChecks,
      publishProfile,
    }, {
      onSuccess: () => {
        closeReviewModal();
        setSelectedSession(null);
      },
    });
  };

  const handleApprove = () => {
    if (selectedSession) {
      approveSession(selectedSession._id, {
        onSuccess: () => {
          closeReviewModal();
          setSelectedSession(null);
        },
      });
    }
  };

  const handleReject = () => {
    if (selectedSession && rejectReason) {
      if (selectedSession.status === "approved") {
        requestVerificationChanges(
          { sessionId: selectedSession._id, reason: rejectReason, checks: verificationChecks },
          {
            onSuccess: () => {
              closeRejectModal();
              closeReviewModal();
              setRejectReason("");
              setSelectedSession(null);
            },
          },
        );
        return;
      }
      rejectSession(
        { sessionId: selectedSession._id, reason: rejectReason },
        {
          onSuccess: () => {
            closeRejectModal();
            closeReviewModal();
            setRejectReason("");
            setSelectedSession(null);
          },
        }
      );
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "invited":
        return <Badge color="gray">{t("invited")}</Badge>;
      case "account_created":
      case "profile_draft":
      case "docs_uploaded":
        return <Badge color="blue">{t("in-progress")}</Badge>;
      case "pending_review":
        return <Badge color="yellow">{t("pending-review")}</Badge>;
      case "approved":
        return <Badge color="green">{t("approved")}</Badge>;
      case "rejected":
        return <Badge color="red">{t("rejected")}</Badge>;
      default:
        return <Badge color="gray">{status}</Badge>;
    }
  };

  const getFileUri = (fileName: string) => accountDocumentUrl(fileName, token);

  const handlePreview = (fileName: string) => {
    if (!fileName) return;
    const uri = getFileUri(fileName);
    const fileType = fileName.toLowerCase().endsWith(".pdf") ? "application/pdf" : "image/jpeg";
    
    setPreviewDocs([
      {
        uri,
        fileType,
        fileName,
      }
    ]);
    openPreviewModal();
  };

  const handleDownload = async (fileName: string) => {
    if (!fileName) return;
    try {
      const downloadUrl = getFileUri(fileName);
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName.split('/').pop() || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      const downloadUrl = getFileUri(fileName);
      window.open(downloadUrl, '_blank');
    }
  };

  if (isLoading) return <AppLoader />;

  return (
    <StyledVerificationQueueTab>
      <Group justify="space-between" mb="lg" align="flex-end">
        <Text fw={500} size="lg">{t("onboarding-sessions")}</Text>
        <Group>
          <Select
            label="Onboarding status"
            placeholder={t("filter-by-status")}
            value={statusFilter}
            onChange={setStatusFilter}
            data={[
              { value: "all", label: t("all") },
              { value: "invited", label: t("invited") },
              { value: "account_created", label: "Account created" },
              { value: "profile_draft", label: "Profile draft" },
              { value: "docs_uploaded", label: "Documents uploaded" },
              { value: "pending_review", label: t("pending-review") },
              { value: "approved", label: t("approved") },
              { value: "rejected", label: t("rejected") },
            ]}
            clearable
            w={200}
          />
          <Select
            label="Verification status"
            value={verificationFilter}
            onChange={setVerificationFilter}
            data={[
              { value: "all", label: t("all") },
              { value: "pending", label: "Pending verification" },
              { value: "verified", label: "Verified" },
              { value: "changes_requested", label: "Changes requested" },
            ]}
            clearable
            w={210}
          />
        </Group>
      </Group>

      <div className="table-container">        <ScrollArea>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("queue-doctor-name")}</Table.Th>
                <Table.Th>{t("queue-email")}</Table.Th>
                <Table.Th>{t("queue-specialization")}</Table.Th>
                <Table.Th>{t("queue-submitted")}</Table.Th>
                <Table.Th>
                  <Tooltip label={t("queue-onboarding-clicks-tooltip")}>
                    <Group gap={4}>
                      <IconMouse size={14} />
                      <Text size="xs" fw={700}>{t("queue-clicks")}</Text>
                    </Group>
                  </Tooltip>
                </Table.Th>
                <Table.Th>{t("queue-status")}</Table.Th>
                <Table.Th>Verification</Table.Th>
                <Table.Th>{t("queue-actions")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {queueData && queueData.length > 0 ? (
                queueData.map((session: any) => (
                  <Table.Tr key={session._id}>
                    <Table.Td>
                      <Text fw={500}>{`${session.first_name} ${session.last_name}`}</Text>
                    </Table.Td>
                    <Table.Td>{session.email}</Table.Td>
                    <Table.Td>{specializationLabel(session)}</Table.Td>
                    <Table.Td>{dayjs(session.updated_at).format("MMM D, YYYY h:mm A")}</Table.Td>
                    <Table.Td>
                      <Badge 
                        variant="dot" 
                        color={session.clicked_at?.length > 0 ? "blue" : "gray"}
                        size="sm"
                      >
                        {session.clicked_at?.length || 0}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {getStatusBadge(session.status)}
                    </Table.Td>
                    <Table.Td>
                      <Badge color={session.verification_status === "verified" ? "green" : session.verification_status === "changes_requested" ? "red" : "yellow"}>
                        {session.verification_status || "pending"}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Button 
                        variant="light" 
                        size="xs" 
                        onClick={() => handleRowClick(session)}
                      >
                        {session.status === "pending_review" ? t("queue-review") : t("view")}
                      </Button>
                    </Table.Td>                  </Table.Tr>
                ))
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={8} align="center">
                    <Text c="dimmed" my="lg">{t("queue-no-pending")}</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </div>

      {/* Review Modal */}
      <Modal
        opened={isReviewModalOpen}
        onClose={closeReviewModal}
        title={<Text fw={600} size="lg">{t("queue-review-app")}: Dr. {selectedSession?.last_name}</Text>}
        size="80%"
      >
        {selectedSession && (
          <Group align="flex-start" grow>
            {/* Left Column: Details */}
            <Stack gap="md" style={{ flex: 1 }}>
              <Box>
                <Text size="sm" c="dimmed">{t("queue-full-name")}</Text>
                <Text fw={500}>{selectedSession.first_name} {selectedSession.last_name}</Text>
              </Box>
              
              <Box>
                <Text size="sm" c="dimmed">{t("queue-email-phone")}</Text>
                <Text>{selectedSession.email}</Text>
                <Text>{selectedSession.phone_number}</Text>
              </Box>

              <Box>
                <Text size="sm" c="dimmed">{t("queue-location")}</Text>
                <Text>{selectedSession.location}</Text>
              </Box>

              <Box>
                <Text size="sm" c="dimmed">{t("queue-prof-category")}</Text>
                <Text fw={500}>
                  {selectedSession.professional_category === "Other Healthcare Professional" 
                    ? selectedSession.professional_category_other 
                    : selectedSession.professional_category}
                </Text>
              </Box>

              <Box>
                <Text size="sm" c="dimmed">{t("queue-prof-title")}</Text>
                {selectedSession.status === "approved" ? <Text>{specializationLabel(selectedSession)}</Text> :
                  <Select disabled={!can("access.manage")} searchable data={primarySpecializationOptions(primarySpecializations)}
                    value={selectedSession.primary_specialization_id || null}
                    placeholder={specializationLabel(selectedSession)}
                    onChange={async value => {
                      if (!value) return;
                      try {
                        await api.patch(`primary-specializations/onboarding/${selectedSession._id}`, {
                          primary_specialization_id: value,
                        });
                        setSelectedSession({ ...selectedSession, primary_specialization_id: value });
                        setSpecializationError("");
                      } catch (reason: any) {
                        setSpecializationError(reason?.response?.data?.detail || "Could not update specialization");
                      }
                    }} />}
                {specializationError && <Text size="sm" c="red">{specializationError}</Text>}
              </Box>

              <Box>
                <Text size="sm" c="dimmed">{t("queue-clinical-specialties")}</Text>
                <Group gap={4} mt={4}>
                  {selectedSession.specialties?.map((spec: any, idx: number) => (
                    <Badge key={idx} variant="outline">{typeof spec === 'object' ? spec.label : spec}</Badge>
                  ))}
                </Group>
              </Box>

              <Box>
                <Text size="sm" c="dimmed">{t("queue-languages")}</Text>
                <Text>{selectedSession.languages?.join(", ")}</Text>
              </Box>

              <Box>
                <Text size="sm" c="dimmed">Licensed Countries</Text>
                <Group gap={4} mt={4}>
                  {selectedSession.licensed_countries?.map((country: string, idx: number) => (
                    <Badge key={idx} variant="light" color="blue">{country === "WW" ? "Worldwide" : country}</Badge>
                  ))}
                </Group>
              </Box>

              <Box>
                <Text size="sm" c="dimmed">{t("queue-consultation-modes")}</Text>
                <Text style={{ textTransform: "capitalize" }}>{selectedSession.consultation_modes?.join(", ").replace(/_/g, " ")}</Text>
              </Box>

              <Box>
                <Text size="sm" c="dimmed">{t("queue-bio")}</Text>
                <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{selectedSession.bio}</Text>
              </Box>

              <Box>
                <Text size="sm" c="dimmed">Years of experience</Text>
                <Text fw={500}>{selectedSession.years_of_experience ?? "-"}</Text>
              </Box>

              <Box>
                <Text size="sm" c="dimmed" mb={6}>Services and prices</Text>
                <Stack gap={6}>
                  {selectedSession.services?.map((service: any) => (
                    <Group key={service.id || service.name} justify="space-between" p="xs" style={{ border: "1px solid #eee", borderRadius: 6 }}>
                      <Box>
                        <Text size="sm" fw={600}>{service.name}</Text>
                        <Text size="xs" c="dimmed">{service.category || "teleconsultation"} · {service.duration_minutes || service.duration || 30} min</Text>
                      </Box>
                      <Text size="sm" fw={600}>{Number(service.price || 0).toLocaleString()} {service.currency || "XOF"}</Text>
                    </Group>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Text size="sm" c="dimmed" mb={6}>Weekly availability</Text>
                <Stack gap={4}>
                  {Object.entries(selectedSession.availability_template?.daily_schedule || {}).map(([day, blocks]: [string, any]) => (
                    <Text key={day} size="sm">
                      Day {day}: {(blocks || []).map((block: any) => `${block.start_time}–${block.end_time}`).join(", ")}
                    </Text>
                  ))}
                </Stack>
              </Box>

              <Box mt="md">
                <Text size="sm" c="dimmed">{t("queue-link-tracking")}</Text>
                <Group gap="xs" mt={4}>
                  <Badge 
                    leftSection={<IconMouse size={12} />} 
                    variant="light" 
                    color={selectedSession.clicked_at?.length > 0 ? "blue" : "gray"}
                  >
                    {selectedSession.clicked_at?.length || 0} {t("queue-total-clicks")}
                  </Badge>
                  {selectedSession.clicked_at?.length > 0 && (
                    <Text size="xs" c="dimmed">
                      {t("queue-last-click")}: {dayjs(selectedSession.clicked_at[selectedSession.clicked_at.length - 1]).format("MMM D, YYYY h:mm A")}
                    </Text>
                  )}
                </Group>
              </Box>

              {can("access.manage") && <Box mt="lg" p="md" style={{ background: "#f8f9fa", borderRadius: "8px", border: "1px solid #e9ecef" }}>
                <Text size="sm" c="dimmed" fw={600} mb="xs">{t("queue-payment-prefs")}</Text>
                {selectedSession.payout_method === "bank_transfer" ? (
                  <>
                    <Text size="sm"><Text span c="dimmed">{t("queue-method")}:</Text> {t("queue-bank-transfer")}</Text>
                    <Text size="sm"><Text span c="dimmed">{t("queue-bank")}:</Text> {selectedSession.bank_name}</Text>
                    <Text size="sm"><Text span c="dimmed">{t("queue-account-iban")}:</Text> {selectedSession.bank_account_number}</Text>
                  </>
                ) : selectedSession.payout_method === "mobile_money" ? (
                  <>
                    <Text size="sm"><Text span c="dimmed">{t("queue-method")}:</Text> {t("queue-mobile-money")}</Text>
                    <Text size="sm"><Text span c="dimmed">{t("queue-provider")}:</Text> {selectedSession.mobile_money_provider}</Text>
                    <Text size="sm"><Text span c="dimmed">{t("queue-number")}:</Text> {selectedSession.mobile_money_number}</Text>
                  </>
                ) : (
                  <Text size="sm" c="dimmed">{t("queue-none-provided")}</Text>
                )}
              </Box>}
            </Stack>

            {/* Right Column: Documents */}
            <Stack gap="md" style={{ flex: 1 }}>
              <Alert icon={<IconAlertCircle size={16} />} color="blue">
                {t(t("queue-verify-license"))}
              </Alert>

              <Box>
                <Text size="sm" c="dimmed">{t(t("queue-license-number"))}</Text>
                <Text fw={700} size="lg" c="blue">{selectedSession.license_number}</Text>
              </Box>

              <Box mt="sm">
                <Text size="sm" c="dimmed" mb={8}>{t(t("queue-uploaded-docs"))}</Text>
                <Stack gap="sm">
                  {selectedSession.medical_license_url && (
                    <Group justify="space-between" style={{ border: "1px solid #eee", padding: "12px", borderRadius: "8px" }}>
                      <Group gap="sm">
                        <IconFileText size={24} color="var(--mantine-color-blue-6)" />
                        <Box>
                          <Text size="sm" fw={500}>{t(t("queue-medical-license"))}</Text>
                          <Text size="xs" c="dimmed">{selectedSession.medical_license_url.split('/').pop()}</Text>
                        </Box>
                      </Group>
                      <Group gap="xs">
                        <Tooltip label={t(t("queue-preview-doc"))} withArrow position="top">
                          <ActionIcon variant="light" color="blue" size="lg" onClick={() => handlePreview(selectedSession.medical_license_url)}>
                            <IconEye size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t(t("queue-download-doc"))} withArrow position="top">
                          <ActionIcon variant="light" color="blue" size="lg" onClick={() => handleDownload(selectedSession.medical_license_url)}>
                            <IconDownload size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Group>
                  )}
                  {selectedSession.proof_of_qualification_url && (
                    <Group justify="space-between" style={{ border: "1px solid #eee", padding: "12px", borderRadius: "8px" }}>
                      <Group gap="sm">
                        <IconId size={24} color="var(--mantine-color-teal-6)" />
                        <Box>
                          <Text size="sm" fw={500}>{t(t("queue-proof-qual"))}</Text>
                          <Text size="xs" c="dimmed">{selectedSession.proof_of_qualification_url.split('/').pop()}</Text>
                        </Box>
                      </Group>
                      <Group gap="xs">
                        <Tooltip label={t(t("queue-preview-doc"))} withArrow position="top">
                          <ActionIcon variant="light" color="blue" size="lg" onClick={() => handlePreview(selectedSession.proof_of_qualification_url)}>
                            <IconEye size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t(t("queue-download-doc"))} withArrow position="top">
                          <ActionIcon variant="light" color="blue" size="lg" onClick={() => handleDownload(selectedSession.proof_of_qualification_url)}>
                            <IconDownload size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Group>
                  )}
                  {selectedSession.profile_photo_url && (
                    <Group justify="space-between" style={{ border: "1px solid #eee", padding: "12px", borderRadius: "8px" }}>
                      <Group gap="sm">
                        <IconId size={24} color="var(--mantine-color-grape-6)" />
                        <Box>
                          <Text size="sm" fw={500}>{t(t("queue-profile-photo"))}</Text>
                          <Text size="xs" c="dimmed">{selectedSession.profile_photo_url.split('/').pop()}</Text>
                        </Box>
                      </Group>
                      <Group gap="xs">
                        <Tooltip label={t(t("queue-preview-doc"))} withArrow position="top">
                          <ActionIcon variant="light" color="blue" size="lg" onClick={() => handlePreview(selectedSession.profile_photo_url)}>
                            <IconEye size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t(t("queue-download-doc"))} withArrow position="top">
                          <ActionIcon variant="light" color="blue" size="lg" onClick={() => handleDownload(selectedSession.profile_photo_url)}>
                            <IconDownload size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Group>
                  )}
                  {selectedSession.signature_url && (
                    <Group justify="space-between" style={{ border: "1px solid #eee", padding: "12px", borderRadius: "8px" }}>
                      <Group gap="sm">
                        <IconId size={24} color="var(--mantine-color-orange-6)" />
                        <Box>
                          <Text size="sm" fw={500}>{t(t("queue-signature-img"))}</Text>
                          <Text size="xs" c="dimmed">{selectedSession.signature_url.split('/').pop()}</Text>
                        </Box>
                      </Group>
                      <Group gap="xs">
                        <Tooltip label={t(t("queue-preview-doc"))} withArrow position="top">
                          <ActionIcon variant="light" color="blue" size="lg" onClick={() => handlePreview(selectedSession.signature_url)}>
                            <IconEye size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t(t("queue-download-doc"))} withArrow position="top">
                          <ActionIcon variant="light" color="blue" size="lg" onClick={() => handleDownload(selectedSession.signature_url)}>
                            <IconDownload size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Group>
                  )}                </Stack>
              </Box>

              <Box mt="lg" p="md" style={{ background: "#e8f5e9", borderRadius: "8px", border: "1px solid #c8e6c9" }}>
                <Text size="sm" c="dimmed" fw={600} mb="xs">{t(t("queue-provider-contract"))}</Text>
                {selectedSession.signed_contract_url ? (
                  <>
                    <Text size="sm" mb="sm">
                      <Text span fw={500}>{t(t("queue-signed-by"))}:</Text> {selectedSession.signature_name}
                    </Text>
                    <Group justify="space-between" style={{ background: "white", padding: "12px", borderRadius: "8px", border: "1px solid #eee" }}>
                      <Group gap="sm">
                        <IconStethoscope size={24} color="var(--mantine-color-green-6)" />
                        <Box>
                          <Text size="sm" fw={500}>{t(t("queue-signed-contract"))}</Text>
                          <Text size="xs" c="dimmed">{t(t("queue-pdf-document"))}</Text>
                        </Box>
                      </Group>
                      <Group gap="xs">
                        <Tooltip label={t(t("queue-preview-doc"))} withArrow position="top">
                          <ActionIcon variant="light" color="blue" size="lg" onClick={() => handlePreview(selectedSession.signed_contract_url)}>
                            <IconEye size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t(t("queue-download-doc"))} withArrow position="top">
                          <ActionIcon variant="light" color="blue" size="lg" onClick={() => handleDownload(selectedSession.signed_contract_url)}>
                            <IconDownload size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Group>
                  </>
                ) : (
                  <Text size="sm" c="red">{t(t("queue-contract-not-signed"))}</Text>
                )}
              </Box>
            </Stack>
          </Group>
        )}

        {selectedSession?.status === "approved" && (
          <Box mt="xl" p="md" style={{ border: "1px solid #e9ecef", borderRadius: 8 }}>
            <Text fw={600} mb="sm">Verification checklist</Text>
            <Stack gap="xs">
              {checklist.map(([key, label]) => (
                <Checkbox
                  key={key}
                  disabled={!can("verification.review")}
                  label={label}
                  checked={verificationChecks[key] === true}
                  onChange={(event) => setVerificationChecks(current => ({
                    ...current,
                    [key]: event.currentTarget.checked,
                  }))}
                />
              ))}
              <Divider my="xs" />
              <Switch
                disabled={!can("profiles.publish")} label="Publish profile after verification"
                checked={publishProfile}
                onChange={(event) => setPublishProfile(event.currentTarget.checked)}
              />
            </Stack>
          </Box>
        )}

        {can("access.manage") && selectedSession?.status === "pending_review" && (
          <Group justify="flex-end" mt="xl" pt="md" style={{ borderTop: "1px solid #eee" }}>
            <Button 
              color="red" 
              variant="outline" 
              leftSection={<IconX size={16} />} 
              onClick={openRejectModal}
              disabled={isApproving}
            >
              {t(t("queue-reject-app"))}
            </Button>
            <Button 
              leftSection={<IconCheck size={16} />} 
              onClick={handleApprove}
              loading={isApproving}
            >
              {t(t("queue-approve-create"))}
            </Button>
          </Group>
        )}
        {can("verification.review") && selectedSession?.status === "approved" && selectedSession?.verification_status !== "verified" && (
          <Group justify="flex-end" mt="xl" pt="md" style={{ borderTop: "1px solid #eee" }}>
            <Button
              color="red"
              variant="outline"
              leftSection={<IconX size={16} />}
              onClick={openRejectModal}
              disabled={isVerifying || (selectedSession?.profile_visibility === "public" && !can("profiles.publish"))}
            >
              Request changes
            </Button>
            <Button
              leftSection={<IconCheck size={16} />}
              onClick={handleVerify}
              loading={isVerifying}
              disabled={!checklist.every(([key]) => verificationChecks[key] === true)}
            >
              Verify{publishProfile ? " and publish" : ""}
            </Button>
          </Group>
        )}
        </Modal>
      {/* Preview Modal */}
      <FilePreviewModal
        opened={isPreviewModalOpen}
        close={closePreviewModal}
        docs={previewDocs}
      />

      {/* Reject Modal */}
      <Modal
        opened={isRejectModalOpen && can(selectedSession?.status === "approved" ? "verification.review" : "access.manage")}
        onClose={closeRejectModal}
        title={<Text fw={600} c="red">{selectedSession?.status === "approved" ? "Request verification changes" : t(t("queue-reject-app"))}</Text>}
      >
        <Stack gap="md">
          <Text size="sm">
            {t(t("queue-reject-reason-desc"))}
          </Text>
          <Textarea
            label={t(t("queue-rejection-reason"))}
            placeholder={t(t("queue-rejection-placeholder"))}
            minRows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
            withAsterisk
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeRejectModal}>{t(t("queue-cancel"))}</Button>
            <Button color="red" onClick={handleReject} loading={isRejecting || isRequestingChanges} disabled={!rejectReason.trim()}>
              {selectedSession?.status === "approved" ? "Send feedback" : t(t("queue-confirm-rejection"))}
            </Button>
          </Group>
        </Stack>
      </Modal>

    </StyledVerificationQueueTab>
  );
};
